/**
 * conversations.type of a forwarded email mirrored from a Listener genie's
 * listen-only mailbox (webhooks/mail-listen.ts). The team replies either from
 * the genie's forwarding address (sent through HG Mail) or from their own
 * inbox, through a prefilled compose window.
 */
export const EMAIL_CONVERSATION_TYPE = "email";

export const isListenedEmail = (type?: string | null): boolean => type === EMAIL_CONVERSATION_TYPE;

/** Where "Reply from my inbox" opens the compose window. */
export type ReplyMailApp = "gmail" | "outlook" | "outlook_personal" | "mailto";

export const REPLY_MAIL_APP_LABELS: Record<ReplyMailApp, string> = {
  gmail: "Gmail or Google Workspace",
  outlook: "Outlook (Microsoft 365)",
  outlook_personal: "Outlook.com or Hotmail",
  mailto: "My default mail app",
};

const OUTLOOK_PERSONAL_DOMAIN_RE = /^(outlook|hotmail|live|msn)\.[a-z.]+$/;

/** The mail app a forwarding inbox runs on, when its domain gives it away; null for company domains. */
export const detectReplyMailApp = (inbox: string | null | undefined): ReplyMailApp | null => {
  const domain = inbox?.split("@")[1]?.toLowerCase();
  if (!domain) return null;
  if (domain === "gmail.com" || domain === "googlemail.com") return "gmail";
  if (OUTLOOK_PERSONAL_DOMAIN_RE.test(domain)) return "outlook_personal";
  return null;
};

/**
 * A compose window in the client's own mail app, addressed to the customer
 * with the reply filled in, so it goes out from their own address. Gmail's
 * authuser picks the forwarding inbox's account when several are signed in.
 */
export const buildReplyComposeUrl = (
  app: ReplyMailApp,
  reply: { inbox: string | null; to: string; subject: string; body: string },
): string => {
  const subject = /^re:/i.test(reply.subject) ? reply.subject : `Re: ${reply.subject}`;
  const params = (entries: Record<string, string>) => new URLSearchParams(entries).toString();
  switch (app) {
    case "gmail":
      return `https://mail.google.com/mail/?${params({
        view: "cm",
        fs: "1",
        ...(reply.inbox ? { authuser: reply.inbox } : {}),
        to: reply.to,
        su: subject,
        body: reply.body,
      })}`;
    case "outlook":
      return `https://outlook.office.com/mail/deeplink/compose?${params({ to: reply.to, subject, body: reply.body })}`;
    case "outlook_personal":
      return `https://outlook.live.com/mail/0/deeplink/compose?${params({ to: reply.to, subject, body: reply.body })}`;
    case "mailto":
      return `mailto:${encodeURIComponent(reply.to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(reply.body)}`;
  }
};
